/* eslint-disable no-console */
import * as moment from 'moment'
import 'moment-timezone'
import { STATUSES } from '@/shared/constants/questionnaire'
import { COLORS } from '@/shared/constants/colors'

const capitalize = str => {
  if (str) {
    const lowercaseStr = str.toLowerCase()
    return lowercaseStr.replace(/(^\w|\s\w|\d^\w)/g, match => match.toUpperCase())
  }
  return ''
}

const snakeCaseToTitleString = str => {
  if (str) {
    return str.replace(/^_*(.)|_+(.)/g, (s, c, d) => (c ? c.toUpperCase() : ` ${d.toUpperCase()}`))
  }
  return ''
}

const onConvertCamelCase = text => {
  const result = text.replace(/([A-Z])/g, ' $1')
  const subFinalResult = result.charAt(0) + result.slice(1)
  const finalResult = subFinalResult.replace(/(?:^|\s)\S/g, data => data)
  return finalResult
}

const isImageFile = file => file.type.match(/image.*/)

/**
 * 
 * Will resize an image to a max size, returning a promise that resolves to a blob that contains resized image
 * @param {*} settings {file, maxSize} where file is a file object and maxSize is the max size of the image
 * @returns a promise that resolves to a blob
 */
const resizeImage = settings => {
  const {file, maxSize} = settings
  const reader = new FileReader()
  const image = new Image()
  const canvas = document.createElement('canvas')
  const dataURItoBlob =  dataURI => {
      const bytes = dataURI.split(',')[0].indexOf('base64') >= 0 ?
          atob(dataURI.split(',')[1]) :
          unescape(dataURI.split(',')[1])
      const mime = dataURI.split(',')[0].split(':')[1].split(';')[0]
      const max = bytes.length
      const ia = new Uint8Array(max)
      for (let i = 0; i < max; i+=1)
          ia[i] = bytes.charCodeAt(i)
      return new Blob([ia], { type: mime })
  }
  const resize =  ()=> {
      let {width, height} = image
      if (width > height) {
          if (width > maxSize) {
              height *= maxSize / width
              width = maxSize
          }
      } else if (height > maxSize) {
              width *= maxSize / height
              height = maxSize
          }
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d').drawImage(image, 0, 0, width, height)
      const dataUrl = canvas.toDataURL('image/jpeg')
      return dataURItoBlob(dataUrl)
  }
  return new Promise((resolve, reject) => {
      if (!isImageFile(file)) {
          reject(new Error("Not an image"))
          return
      }
      reader.onload = readerEvent => {
          image.onload =  () => resolve(resize())
          image.src = readerEvent.target.result
      }
      reader.readAsDataURL(file)
  })
}

/*
isNumber test cases

console.log('false case')
console.log(isNumber('nghi'))
console.log(isNumber('01a'))
console.log(isNumber(''))
console.log(isNumber(true))
console.log(isNumber(false))
console.log(isNumber(null))
console.log(isNumber(undefined))

console.log('true case')
console.log(isNumber('0'))
console.log(isNumber(0))
console.log(isNumber(+0))
console.log(isNumber(-0))
console.log(isNumber('01'))
console.log(isNumber(1))
console.log(isNumber(+1))
console.log(isNumber(-1))
*/
const isNumber = input => {
  if(input === null || input === '' || typeof input === 'boolean'){
    return false
  }
  return !Number.isNaN(Number(input))
}

/*
isNonEmptyText test cases

// true
console.log(isNonEmptyText('nghi'))
console.log(isNonEmptyText('0'))

// false
console.log(isNonEmptyText(1))
console.log(isNonEmptyText(0))
console.log(isNonEmptyText(''))
console.log(isNonEmptyText(true))
console.log(isNonEmptyText(false))
console.log(isNonEmptyText(null))
console.log(isNonEmptyText(undefined))

*/
const isNonEmptyText = input => !!(input && input.length >= 1)
const isEmptyText = input => !isNonEmptyText(input)

const isPhoneNumber = input => {
  const isStartingWithPlus = input[0] === '+'
  let result = false
  if (isNonEmptyText(input)) {
    result = isNumber(isStartingWithPlus ? input.slice(1, input.length - 1) : input)
  }
  return result
}

const handleTimezoneDiff = date => {
  if (!date) return ''
  return moment(date).tz('Europe/Helsinki').format('DD.MM.YYYY')
}

const endOfDate = date => {
  if (!date) return ''
  return moment(date).endOf('day')
}

const startOfDate = date => {
  if (!date) return ''
  return moment(date).startOf('day')
}

const isValidURL = string => {
  const pattern = new RegExp('^https?:\\/\\/' // protocol
    + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
    + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
    + '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator

  return !!pattern.test(string)
}

const questionnaireStatusVariant = questionnaire => {
  let variant = 'light-secondary'

  if (!questionnaire.isDraft) {
    const expiration = questionnaire.expirationDate.toDate().getTime()
    const now = new Date().getTime()

    if (now <= expiration) variant = 'light-primary'
    else variant = 'light-danger'
  }

  return variant
}

const questionnaireStatus = questionnaire => {
  let status = STATUSES.DRAFT

  if (!questionnaire.isDraft) {
    const expiration = questionnaire.expirationDate.toDate().getTime()
    const now = new Date().getTime()

    if (now <= expiration) status = STATUSES.ACTIVE
    else status = STATUSES.EXPIRED
  }

  return status
}
const isEmail = input => input.match(
  /^(([^<>()[\]\\.,;:\s@\\"]+(\.[^<>()[\]\\.,;:\s@\\"]+)*)|(\\".+\\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
)

/*
isFullName test cases

// true
console.log(isFullName('nghi le'))
console.log(isFullName('key & peele'))

// false
console.log(isFullName('nghi'))
console.log(isFullName(''))

*/
const isFullName = input => input && input.split(' ').length > 1

/**
 * getInitials() returns the first and last name initials
 * based on the passed-in full-name
 * eg: 'Elon Reeve Musk' -> 'EM'
 */
const getInitials = fullName => {
  if (!fullName) return ''
  const nameArr = fullName.split(' ')
  const nameArrLength = nameArr.length
  const [firstNameInit, lastNameInit] = [nameArr[0].charAt(0), nameArrLength === 1 ? '' : nameArr[nameArrLength - 1].charAt(0)]
  return (firstNameInit + lastNameInit).toUpperCase()
}

const getTargetLabels = item => {
  const labels = []
  if (item.target.state) {
    labels.push(item.target.state)
  }
  if (item.target.role) {
    labels.push(item.target.role)
  }
  if (item.target.city && item.target.city.length > 0) {
    labels.concat(item.target.city)
  }
  return labels
}

const questionnaireTargetLabelsColor = index => [COLORS.BLUE, COLORS.INDIGO, COLORS.RED, COLORS.YELLOW, COLORS.CYAN][index] || COLORS.GRAY

export {
  // TODO better function name + documentations
  capitalize,
  onConvertCamelCase,
  snakeCaseToTitleString,

  handleTimezoneDiff,
  endOfDate,
  startOfDate,

  isEmail,
  isValidURL,
  isFullName,
  isEmptyText,
  isNonEmptyText,
  isPhoneNumber,

  getInitials,
  getTargetLabels,

  // TODO function name should be verbs
  questionnaireStatus,
  questionnaireStatusVariant,
  questionnaireTargetLabelsColor,

  resizeImage,
}
